import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SortAsc, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ProjectCard from '../components/ProjectCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import SearchBar from '../components/SearchBar';
import { useApi } from '../hooks/useApi';
import { categoriesAPI, projectsAPI } from '../api/client';

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialCategory = searchParams.get('category') || '';
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Get categories for filters and modal
  const { data: categories } = useApi(categoriesAPI.getAll);

  // Perform search when query or filters change
  useEffect(() => {
    const performSearch = async () => {
      if (!initialQuery.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const searchResults = await projectsAPI.search(
          initialQuery, 
          categoryFilter || null, 
          null
        );
        setResults(searchResults);
      } catch (err) {
        setError(err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [initialQuery, categoryFilter]);

  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    let filtered = [...results];

    return filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'author':
          comparison = a.author.localeCompare(b.author);
          break;
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'relevance':
          // Simple relevance based on query match in title
          const aRelevance = a.title.toLowerCase().includes(initialQuery.toLowerCase()) ? 1 : 0;
          const bRelevance = b.title.toLowerCase().includes(initialQuery.toLowerCase()) ? 1 : 0;
          comparison = bRelevance - aRelevance;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [results, sortBy, sortOrder, initialQuery]);

  const handleCategoryFilterChange = (value) => {
    const filterValue = value === 'all' ? '' : value;
    setCategoryFilter(filterValue);
    const newParams = new URLSearchParams(searchParams);
    if (filterValue) {
      newParams.set('category', filterValue);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  };

  const getCategoryName = (categoryType) => {
    if (!categories || !categories[categoryType]) return categoryType;
    return categories[categoryType].title;
  };

  const getSubcategoryName = (categoryType, subcategoryId) => {
    if (!categories || !categories[categoryType]) return subcategoryId;
    const subcategory = categories[categoryType].subcategories?.find(sub => sub.id === subcategoryId);
    return subcategory ? subcategory.name : subcategoryId;
  };

  const getCategoryBadgeColor = (categoryType) => {
    switch (categoryType) {
      case 'digital':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'printed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'sites':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-emerald-600 transition-colors">
            Inicio
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">Resultados de búsqueda</span>
        </div>

        {/* Back Button */}
        <Button 
          asChild 
          variant="ghost" 
          className="mb-6 hover:bg-emerald-50 hover:text-emerald-700"
        >
          <Link to="/" className="inline-flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al inicio</span>
          </Link>
        </Button>

        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Resultados de Búsqueda
          </h1>
          
          {/* Search Bar */}
          <div className="mb-6">
            <SearchBar />
          </div>

          {/* Search Info */}
          {initialQuery && (
            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
              <span>
                Mostrando resultados para: <strong>"{initialQuery}"</strong>
              </span>
              {categoryFilter && (
                <Badge className={getCategoryBadgeColor(categoryFilter)}>
                  {getCategoryName(categoryFilter)}
                </Badge>
              )}
              <span className="text-emerald-600 font-medium">
                {filteredAndSortedResults.length} resultado{filteredAndSortedResults.length !== 1 ? 's' : ''} encontrado{filteredAndSortedResults.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Filters and Sort */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrar por categoría
              </label>
              <Select value={categoryFilter || 'all'} onValueChange={handleCategoryFilterChange}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  <SelectItem value="digital">Recursos Digitales</SelectItem>
                  <SelectItem value="printed">Recursos Impresos</SelectItem>
                  <SelectItem value="sites">Sitios de Interés</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordenar por
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevancia</SelectItem>
                    <SelectItem value="date">Fecha</SelectItem>
                    <SelectItem value="title">Título</SelectItem>
                    <SelectItem value="author">Autor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orden
                </label>
                <Button
                  variant="outline"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3"
                >
                  <SortAsc className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {!initialQuery ? (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Realiza una búsqueda
            </h3>
            <p className="text-gray-600">
              Usa la barra de búsqueda para encontrar proyectos, autores o temas específicos.
            </p>
          </div>
        ) : loading ? (
          <LoadingSpinner text="Buscando proyectos..." />
        ) : error ? (
          <ErrorMessage error={error} />
        ) : filteredAndSortedResults.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedResults.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                categoryName={getSubcategoryName(project.categoryType, project.subcategory)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No se encontraron resultados
            </h3>
            <p className="text-gray-600 mb-4">
              No hay proyectos que coincidan con "{initialQuery}"
              {categoryFilter && ` en ${getCategoryName(categoryFilter)}`}.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Sugerencias:</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Verifica la ortografía de las palabras</li>
                <li>• Intenta con términos más generales</li>
                <li>• Usa palabras clave diferentes</li>
                <li>• Intenta sin filtros de categoría</li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SearchResultsPage;